import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { ContentStore, UserTermsAcceptance, UserTermsAcceptanceStore } from '@stores';
import { marked } from 'marked';
import { useAppCookies } from '@app/providers/AppCookieProvider';

interface TermsGuardProps {
  children: React.ReactNode;
}

export const TermsGuard: React.FC<TermsGuardProps> = ({ children }) => {
  const cookies = useAppCookies();
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [terms, setTerms] = useState<{ title: string; content: string; version: number }>({
    title: '',
    content: '',
    version: 0,
  });

  const fetchTermsAndCheck = async () => {
    if (!cookies.profile.available) {
      // If no profile ID, skip terms check. We should never reach here as the caller ensures a profile ID exists.
      return;
    }
    try {
      const [content] = await new ContentStore().readByContentType('termsAndConditions', (item) => ({
        title: String(item.fields['title']),
        content: marked(String(item.fields['content']), { async: false }),
        version: Number(item.sys.publishedVersion),
      }));

      setTerms(content);

      const termsStore = new UserTermsAcceptanceStore();
      const response = await termsStore.read();
      if (response.status === 'SUCCESS') {
        const acceptances = response.result as UserTermsAcceptance[];
        const hasAccepted = acceptances.some((r) => r.termsVersion === String(content.version));
        setShowDialog(!hasAccepted);
      } else {
        setShowDialog(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true); // Start loading state
    if (cookies.auth.available && cookies.profile.available) {
      fetchTermsAndCheck();
    } else {
      setLoading(false); // Not authenticated, skip terms check and stop loading
    }
  }, [cookies.auth.available, cookies.profile.available]);

  const handleAccept = async () => {
    if (cookies.profile.available) {
      const store = new UserTermsAcceptanceStore();
      await store.create({ termsVersion: String(terms.version) });
      setShowDialog(false);
    }
  };

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      {showDialog && (
        <Dialog open>
          <DialogTitle>{terms.title || 'Terms and Conditions'}</DialogTitle>
          <DialogContent>
            <Typography variant="caption" dangerouslySetInnerHTML={{ __html: terms.content }} />
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={handleAccept}>
              Accept and Continue
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {children}
    </>
  );
};
